import { useEffect, useState } from 'react';
import { tr } from '@elec/domain';
import type { Locale, Translate } from '@elec/i18n';
import { Dialog } from './ui.tsx';

/** The AI features, each reserved for signed-in accounts. */
export type AiFeature = 'assist' | 'board' | 'sketch' | 'report';
export const AI_FEATURES: readonly AiFeature[] = ['assist', 'board', 'sketch', 'report'];

/** Time each demo plays before the next one, while the person has not picked one. */
const CYCLE_MS = 5600;

/**
 * Shown when a signed-out person reaches for an AI feature: a short animated demo of what each
 * feature does, then sign in. Opens on the feature they tried; cycles through the others until
 * they pick a tab.
 */
export function AiSignInPrompt(props: {
  feature: AiFeature;
  t: Translate;
  locale: Locale;
  onSignIn(): void;
  onClose(): void;
}) {
  const { t, locale } = props;
  const [shown, setShown] = useState<AiFeature>(props.feature);
  const [auto, setAuto] = useState(true);
  useEffect(() => {
    if (!auto) return;
    const id = setTimeout(() => {
      setShown((f) => AI_FEATURES[(AI_FEATURES.indexOf(f) + 1) % AI_FEATURES.length]!);
    }, CYCLE_MS);
    return () => clearTimeout(id);
  }, [auto, shown]);

  return (
    <Dialog
      title={t('aiGate.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose} data-testid="ai-gate-later">
            {t('aiGate.later')}
          </button>
          <button className="primary" onClick={props.onSignIn} data-testid="ai-gate-signin">
            {t('aiGate.signIn')}
          </button>
        </>
      }
    >
      <div className="aig" data-testid="ai-gate">
        <p className="aig-intro">{t('aiGate.intro')}</p>
        <div className="segmented aig-tabs" role="tablist">
          {AI_FEATURES.map((f) => (
            <button
              key={f}
              role="tab"
              aria-selected={f === shown}
              className={f === shown ? 'active' : ''}
              onClick={() => {
                setAuto(false);
                setShown(f);
              }}
              data-testid={`ai-gate-tab-${f}`}
            >
              {t(`aiGate.tab.${f}`)}
            </button>
          ))}
        </div>
        {/* Keyed so the animation starts over on every change. */}
        <div className="aig-stage" key={shown} data-feature={shown} aria-hidden="true">
          {shown === 'assist' && <AssistDemo locale={locale} apply={t('ai.apply')} />}
          {shown === 'board' && <BoardDemo locale={locale} />}
          {shown === 'sketch' && <SketchDemo />}
          {shown === 'report' && <ReportDemo locale={locale} />}
          {auto && <span className="aig-progress" style={{ animationDuration: `${CYCLE_MS}ms` }} />}
        </div>
        <p className="aig-caption" aria-live="polite">
          {t(`aiGate.cap.${shown}`)}
        </p>
      </div>
    </Dialog>
  );
}

function AssistDemo(props: { locale: Locale; apply: string }) {
  const l = props.locale;
  return (
    <div className="aig-chat">
      <div className="aig-bubble user aig-type">
        {tr(l, 'Passe le circuit C en 16 A', 'Set circuit C to 16 A', 'Zet kring C op 16 A')}
      </div>
      <div className="aig-bubble bot aig-in" style={{ animationDelay: '1.5s' }}>
        <span className="aig-dim">
          {tr(
            l,
            'Circuit C · Prises cuisine',
            'Circuit C · Kitchen sockets',
            'Kring C · Stopcontacten keuken',
          )}
        </span>
        <span className="aig-mono">
          <s>20 A</s> → <b>16 A</b>
        </span>
        <span className="aig-chip aig-pop" style={{ animationDelay: '2.4s' }}>
          {props.apply}
        </span>
      </div>
    </div>
  );
}

function BoardDemo(props: { locale: Locale }) {
  const l = props.locale;
  const rows: Array<[string, string, string]> = [
    ['A', '16 A', tr(l, 'Éclairage', 'Lighting', 'Verlichting')],
    ['B', '20 A', tr(l, 'Prises séjour', 'Living room sockets', 'Stopcontacten woonkamer')],
    ['C', '20 A', tr(l, 'Prises cuisine', 'Kitchen sockets', 'Stopcontacten keuken')],
    ['D', '32 A', tr(l, 'Cuisinière', 'Cooker', 'Kookfornuis')],
  ];
  return (
    <div className="aig-split">
      <div className="aig-photo">
        <svg viewBox="0 0 160 120" width="160" height="120">
          <rect x="4" y="4" width="152" height="112" rx="6" className="aig-box" />
          <rect x="12" y="50" width="136" height="6" className="aig-rail" />
          <rect x="16" y="34" width="34" height="38" rx="2" className="aig-mod rcd" />
          <rect x="24" y="44" width="6" height="10" rx="1" className="aig-lever" />
          {[0, 1, 2, 3].map((i) => (
            <g key={i}>
              <rect x={56 + i * 22} y="34" width="18" height="38" rx="2" className="aig-mod" />
              <rect x={62 + i * 22} y="44" width="6" height="10" rx="1" className="aig-lever" />
            </g>
          ))}
          <rect x="8" y="8" width="144" height="3" className="aig-scan" />
        </svg>
      </div>
      <ul className="aig-list">
        <li className="aig-in head" style={{ animationDelay: '1.6s' }}>
          <span className="aig-mono">
            {tr(l, 'Diff. 300 mA · A · 40 A', 'RCD 300 mA · A · 40 A', 'Verl. 300 mA · A · 40 A')}
          </span>
        </li>
        {rows.map(([code, rating, name], i) => (
          <li key={code} className="aig-in" style={{ animationDelay: `${1.9 + i * 0.3}s` }}>
            <span className="aig-mono aig-code">{code}</span>
            <span className="aig-mono">{rating}</span>
            <span>{name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SketchDemo() {
  return (
    <div className="aig-split">
      <svg viewBox="0 0 150 120" width="150" height="120" className="aig-paper">
        {/* A wobbly hand-drawn single-line diagram. */}
        <path
          pathLength={1}
          className="aig-hand"
          d="M20 22 C40 19 70 25 128 21 M24 21 C23 45 26 70 22 98 M58 23 C57 50 60 72 56 98 M92 22 C94 50 90 74 93 98 M124 22 C123 48 126 72 122 98"
        />
        <path
          pathLength={1}
          className="aig-hand"
          style={{ animationDelay: '0.9s' }}
          d="M16 52 C22 48 28 49 31 54 M50 52 C56 47 62 49 65 54 M85 53 C90 48 96 49 99 54 M117 52 C122 48 128 49 131 54"
        />
      </svg>
      <span className="aig-arrow aig-in" style={{ animationDelay: '1.7s' }}>
        →
      </span>
      <svg
        viewBox="0 0 150 120"
        width="150"
        height="120"
        className="aig-clean aig-in"
        style={{ animationDelay: '2s' }}
      >
        <path className="aig-wire" d="M20 20 H130 M24 20 V100 M58 20 V100 M92 20 V100 M126 20 V100" />
        {[24, 58, 92, 126].map((x) => (
          <g key={x}>
            <rect x={x - 6} y="46" width="12" height="16" rx="1" className="aig-cb" />
            <circle cx={x} cy="100" r="4" className="aig-load" />
          </g>
        ))}
      </svg>
    </div>
  );
}

function ReportDemo(props: { locale: Locale }) {
  const l = props.locale;
  const remarks: Array<['err' | 'warn' | 'ok', string]> = [
    [
      'err',
      tr(
        l,
        'Salle de bain : liaison équipotentielle absente',
        'Bathroom: no supplementary bonding',
        'Badkamer: geen bijkomende equipotentiaalverbinding',
      ),
    ],
    [
      'warn',
      tr(
        l,
        'Schéma unifilaire à mettre à jour',
        'Single-line diagram to update',
        'Eendraadsschema bijwerken',
      ),
    ],
    [
      'ok',
      tr(l, 'Différentiel 30 mA : test OK', '30 mA RCD: test OK', 'Verliesstroomschakelaar 30 mA: test OK'),
    ],
  ];
  return (
    <div className="aig-split">
      <div className="aig-page">
        {[70, 90, 55, 85, 64, 80, 48].map((w, i) => (
          <span
            key={i}
            className={`aig-line${i === 1 || i === 3 || i === 5 ? ' hit' : ''}`}
            style={{ width: `${w}%`, animationDelay: `${0.5 + i * 0.12}s` }}
          />
        ))}
        <span className="aig-scan" />
      </div>
      <ul className="aig-list">
        {remarks.map(([level, text], i) => (
          <li key={text} className="aig-in" style={{ animationDelay: `${1.6 + i * 0.4}s` }}>
            <span className={`aig-dot ${level}`} />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
